import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, Loader2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";

export default function PowerTranzDialog({ open, booking, onClose, onSuccess }) {
    const { t } = useT();
    const { format: formatMoney } = useCurrency();
    const [step, setStep] = useState("card"); // card | threeds
    const [redirectData, setRedirectData] = useState("");
    const [processing, setProcessing] = useState(false);
    const [card, setCard] = useState({
        pan: "", exp_month: "", exp_year: "", cvv: "", name: booking?.guest_name || "",
    });

    useEffect(() => {
        if (step !== "threeds") return;
        const handler = (event) => {
            const data = event.data || {};
            if (data.type === "PT_3DS_COMPLETE") {
                if (data.status === "success") {
                    onSuccess();
                } else {
                    toast.error(t("book.paymentFailed"));
                    setStep("card");
                    setProcessing(false);
                }
            }
        };
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, [step, onSuccess, t]);

    const pay = async () => {
        if (!card.pan || !card.exp_month || !card.exp_year || !card.cvv) {
            toast.error(t("book.cardIncomplete"));
            return;
        }
        setProcessing(true);
        try {
            const r = await api.post("/payments/powertranz/session", { booking_id: booking.id, card });
            if (r.data.redirect_data) {
                setRedirectData(r.data.redirect_data);
                setStep("threeds");
            } else {
                toast.error(t("book.paymentFailed"));
                setProcessing(false);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t("book.paymentFailed"));
            setProcessing(false);
        }
    };

    const set = (k, v) => setCard((c) => ({ ...c, [k]: v }));

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="powertranz-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl">{t("book.cardEntry")}</DialogTitle>
                    <DialogDescription>{booking?.cabin_name} · {formatMoney(booking?.total_price || 0)}</DialogDescription>
                </DialogHeader>

                {step === "card" ? (
                    <CardForm card={card} set={set} pay={pay} processing={processing} total={booking?.total_price || 0} formatMoney={formatMoney} t={t} />
                ) : (
                    <div className="h-[460px]" data-testid="powertranz-3ds">
                        <iframe
                            title="3-D Secure"
                            srcDoc={redirectData}
                            sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation"
                            className="w-full h-full border border-espresso/15"
                        />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function CardForm({ card, set, pay, processing, total, formatMoney, t }) {
    return (
        <div className="space-y-4" data-testid="powertranz-card-form">
            <Input label={t("book.cardName")} value={card.name} onChange={(v) => set("name", v)} testid="pt-name" />
            <Input label={t("book.cardNumber")} value={card.pan} onChange={(v) => set("pan", v.replace(/[^\d ]/g, ""))} testid="pt-pan" placeholder="4111 1111 1111 1111" />
            <div className="grid grid-cols-3 gap-3">
                <Input label={t("book.cardExpM")} value={card.exp_month} onChange={(v) => set("exp_month", v.replace(/\D/g, "").slice(0, 2))} testid="pt-exp-m" placeholder="MM" />
                <Input label={t("book.cardExpY")} value={card.exp_year} onChange={(v) => set("exp_year", v.replace(/\D/g, "").slice(0, 4))} testid="pt-exp-y" placeholder="YY" />
                <Input label={t("book.cardCvv")} value={card.cvv} onChange={(v) => set("cvv", v.replace(/\D/g, "").slice(0, 4))} testid="pt-cvv" placeholder="123" />
            </div>
            <button
                onClick={pay}
                disabled={processing}
                data-testid="pt-pay-btn"
                className="w-full px-6 py-4 bg-forest text-bone hover:bg-forestDark text-xs uppercase tracking-widest2 font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
                {processing ? <><Loader2 size={14} className="animate-spin" /> {t("book.paying")}</>
                    : <><Lock size={13} /> {t("book.payNow", formatMoney(total))}</>}
            </button>
            <p className="text-[11px] text-muted2 inline-flex items-center gap-1.5 justify-center w-full">
                <ShieldCheck size={12} className="text-forest" /> {t("book.securePayment")}
            </p>
        </div>
    );
}

function Input({ label, value, onChange, testid, placeholder, type = "text" }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-widest2 text-espresso/80 mb-1">{label}</label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                placeholder={placeholder}
                inputMode="numeric"
                className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent font-mono focus:border-forest outline-none"
            />
        </div>
    );
}
